// Колонка пунктов «Ещё» прямо на главном (E10-T04; просьба владельца: «все активные пункты вывел бы»). Пункты —
// только активные: экран есть и флаг не выключен (Пропуск, События, Рейтинги, Журнал — пока их экранов нет — не
// показываются вовсе). Колонка всегда у правого края, сразу под шапкой — без прыжков между экранами, размерами и
// уровнями зон (замечание владельца). Что не влезло по высоте — в «Ещё», на левый край не переносится. Метки зон,
// на которые колонка налезает, сдвигает слой зон (garage.tsx) по railBox, а не колонка.
// Полноэкранный Telegram: верх колонки — под шапкой (она уже ниже safe-area и системных кнопок), бок — safe-right.
import { signal } from '@preact/signals';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useApp } from '../app/app.js';
import { t as tr } from '../i18n/t.js';
import { assetEntry } from '../assets/manifest.js';
import { Icon } from '../assets/ui.js';
import { ICONS, type IconName } from '../assets/icons.js';
import { legalUrls } from '../state/legal.js';
import { mailUnclaimed } from '../state/quests.js';
import { viewport } from '../telegram/viewport.js';
import '../ui/store.css';

export interface MoreItem {
  key: string;
  /** Свой значок пункта (если арт его прислал) и запасной из уже нарисованных — без эмодзи. */
  icon: string;
  alt: IconName;
  badge?: number;
  testId: string;
  go: () => void;
}

/** Активные пункты «Ещё» в порядке ux/03 §11. Неактивных (нет экрана или флаг выключен) в списке нет. */
export function useMoreItems(): MoreItem[] {
  const app = useApp();
  // Р-102: при legal.docs.visible = false сервер присылает legal.urls = {} — пункта «Правила» нет.
  const legalOn = Object.keys(legalUrls()).length > 0;
  const items: (MoreItem | false)[] = [
    { key: 'CL-MORE-MAIL', icon: 'ICO-MAIL', alt: 'mail', badge: mailUnclaimed.value, testId: 'more-mail', go: () => app.nav.push('SCR-MAIL') },
    { key: 'CL-FRIENDS-TITLE', icon: 'ICO-FRIENDS', alt: 'hand', testId: 'more-friends', go: () => app.nav.push('SCR-FRIENDS') },
    { key: 'CL-STORE-PURCHASES', icon: 'ICO-PURCHASES', alt: 'shop', testId: 'more-purchases', go: () => { app.nav.selectTab('shop'); app.nav.push('SCR-PURCHASES'); } },
    { key: 'CL-MORE-SETTINGS', icon: 'ICO-SETTINGS', alt: 'sound', testId: 'more-settings', go: () => app.nav.openService('SCR-SETTINGS') },
    { key: 'CL-MORE-SUPPORT', icon: 'ICO-SUPPORT', alt: 'semenych', testId: 'more-support', go: () => app.nav.openService('SCR-SUPPORT') },
    legalOn && { key: 'CL-MORE-LEGAL', icon: 'ICO-LEGAL', alt: 'check', testId: 'more-legal', go: () => app.nav.openService('SCR-LEGAL') },
  ];
  return items.filter((x): x is MoreItem => !!x);
}

export function MoreIcon(p: { item: MoreItem; size: number }) {
  // Своего значка пункта в манифесте нет — нарисованный запасной из набора (ICO-*), а не эмодзи.
  return assetEntry(p.item.icon) ? <Icon id={p.item.icon} fallback="" size={p.size} /> : <Icon id={ICONS[p.item.alt][0]} fallback="" size={p.size} />;
}

/** Прямоугольник колонки в координатах окна: слой зон сдвигает влево метки, которые в него заходят. */
export const railBox = signal<{ left: number; top: number; bottom: number } | null>(null);

/** Верхние края ячеек колонки сверху вниз: подряд от `top`, пока ячейка высотой `h` влезает в `height`. */
export function railSlots(height: number, h: number, gap = 4, top = 4): number[] {
  const out: number[] = [];
  for (let y = top; y + h <= height - 4; y += h + gap) out.push(y);
  return out;
}

/** Что показать в колонке: все пункты, если влезают; иначе n−1 пунктов и «Ещё» с остальными. */
export function splitRail<T>(items: T[], slots: number): { shown: T[]; rest: T[] } {
  if (items.length <= slots) return { shown: items, rest: [] };
  const k = Math.max(0, slots - 1);
  return { shown: items.slice(0, k), rest: items.slice(k) };
}

type Box = { left: number; right: number; top: number; bottom: number };

/** Сдвиг метки влево, чтобы её правый край не заходил в колонку (с зазором `gap`); не левее `minLeft`. */
export function labelShift(label: Box, rail: { left: number; top: number; bottom: number }, gap = 4, minLeft = 4): number {
  if (label.bottom <= rail.top || label.top >= rail.bottom) return 0;
  const over = label.right - (rail.left - gap);
  return over > 0 ? Math.max(0, Math.min(over, label.left - minLeft)) : 0;
}

/**
 * Сдвиги всех меток: метку, которая заходит в колонку, — влево; если сдвинутая налезла на соседнюю, соседка
 * уходит влево следом (по цепочке, справа налево). Метки, которые никто не двигал, не трогаем.
 */
export function shiftLabels(labels: (Box & { key: string })[], rail: { left: number; top: number; bottom: number }, gap = 4, minLeft = 4): Record<string, number> {
  const out: Record<string, number> = {};
  const moved: Box[] = []; // уже сдвинутые метки на новых местах
  for (const l of [...labels].sort((a, b) => b.right - a.right)) {
    let d = labelShift(l, rail, gap, minLeft);
    for (let again = true; again;) {
      again = false;
      for (const o of moved) {
        const R = l.right - d;
        const hits = o.top < l.bottom && o.bottom > l.top && l.left - d < o.right + gap && R > o.left - gap && l.left < o.left;
        if (hits && l.left - d - (R - o.left + gap) >= minLeft) { d += R - o.left + gap; again = true; }
      }
    }
    if (d > 0) { out[l.key] = Math.round(d); moved.push({ left: l.left - d, right: l.right - d, top: l.top, bottom: l.bottom }); }
  }
  return out;
}

const CELL_W = 76; // «Поддержка», «Настройки» кеглем 12 без многоточия
const CELL_H = 44; // минимум касания
const GAP = 4;

/** Колонка на главном: правый край, под шапкой. */
export function HomeRail() {
  const app = useApp();
  const items = useMoreItems();
  const ref = useRef<HTMLDivElement>(null);
  const [lay, setLay] = useState<{ x: number; slots: number[] } | null>(null);
  const vp = viewport.value;
  useLayoutEffect(() => {
    const host = ref.current?.parentElement;
    if (!host) return;
    const measure = () => {
      const box = host.getBoundingClientRect();
      const safeR = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-right')) || 16;
      const edge = Math.max(4, safeR - 10);
      setLay({ x: box.width - edge - CELL_W, slots: railSlots(box.height, CELL_H, GAP) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, [vp.w, vp.h]);
  const { shown, rest } = splitRail(items, lay?.slots.length ?? 0);
  const cells = [...shown.map((it) => ({ it, more: false })), ...(rest.length ? [{ it: null as MoreItem | null, more: true }] : [])];
  const used = lay ? Math.min(cells.length, lay.slots.length) : 0;
  useLayoutEffect(() => {
    const host = ref.current?.parentElement;
    if (!host || !lay || !used) { railBox.value = null; return; }
    const b = host.getBoundingClientRect();
    const next = { left: b.left + lay.x, top: b.top + lay.slots[0]!, bottom: b.top + lay.slots[used - 1]! + CELL_H };
    const o = railBox.value;
    if (!o || o.left !== next.left || o.top !== next.top || o.bottom !== next.bottom) railBox.value = next;
  }, [lay, used]);
  useLayoutEffect(() => () => { railBox.value = null; }, []);
  return (
    <div class="home-rail" ref={ref} data-testid="home-rail" data-side="right">
      {lay ? cells.map((c, i) => {
        const y = lay.slots[i];
        if (y === undefined) return null;
        const style = { left: `${lay.x}px`, top: `${y}px`, width: `${CELL_W}px`, height: `${CELL_H}px` };
        if (c.more) {
          return (
            <button key="more" class="rail-cell" style={style} data-testid="home-more" aria-label={tr('CL-MORE-TITLE')}
              onClick={() => { app.haptic('light'); app.nav.openSheet('MOD-MORE', { keys: rest.map((r) => r.key) }); }}>
              {assetEntry('ICO-MORE') ? <Icon id="ICO-MORE" fallback="" size={22} /> : <span class="rail-dots" aria-hidden>⋯</span>}<span class="rail-label">{tr('CL-MORE-TITLE')}</span>
              {rest.some((r) => r.badge) ? <span class="nav-badge rail-badge" aria-hidden>!</span> : null}
            </button>
          );
        }
        const it = c.it!;
        return (
          <button key={it.key} class="rail-cell" style={style} data-testid={`rail-${it.testId}`} aria-label={tr(it.key)} onClick={() => { app.haptic('light'); it.go(); }}>
            <MoreIcon item={it} size={22} />
            <span class="rail-label">{tr(it.key)}</span>
            {it.badge ? <span class="nav-badge rail-badge" data-testid={`${it.testId}-badge`}>{it.badge > 9 ? '9+' : it.badge}</span> : null}
          </button>
        );
      }) : null}
    </div>
  );
}
